<template>
  <div class="px-6 px-lg-14 pt-6 pt-lg-10">
    <div class="mx-0 pb-6 pb-lg-10 row align-items-end">
      <div class="text-h4 text-uppercase" style="display: inline-block;">{{ $t("license_management") }}</div>
    </div>
    <div class="row mx-0 justify-content-between">
      <div class="col">

        <v-menu offset-y :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              :ripple="false"
              hide-details="auto"
              v-bind="attrs"
              v-on="on"
              class="rounded-0 mt-2 mr-2"
              outlined
            >
              <v-icon class="mr-1">mdi-filter</v-icon> {{ $t('filter') }}
            </v-btn>
          </template>
          <v-list dense>
            <v-subheader>{{ $t('license_type') }}</v-subheader>
            <v-list-item @click="filterType = null; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterType === null">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('all') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterType = false; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterType === false">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('normal_licenses') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterType = true; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterType === true">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('test_licenses') }}</v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>

            <v-subheader>{{ $t('validity') }}</v-subheader>
            <v-list-item @click="filterExpired = null; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterExpired === null">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('all') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterExpired = false; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterExpired === false">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('active') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterExpired = true; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterExpired === true">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('_expired') }}</v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>

            <v-subheader>{{ $t('assignment_status') }}</v-subheader>
            <v-list-item @click="filterAssigned = null; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterAssigned === null">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('all') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterAssigned = true; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterAssigned === true">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('assigned') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="filterAssigned = false; pagination.page=0; fetchLicenses()">
              <v-list-item-action>
                <v-icon v-if="filterAssigned === false">mdi-check</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('unassigned') }}</v-list-item-title>
            </v-list-item>

            <v-divider></v-divider>

            <div class="px-4 py-2" v-if="rightGlobalAccess">
               <v-autocomplete
                  v-model="filterTenant"
                  :items="customerItems"
                  :search-input.sync="filterCustomerSearch"
                  :label="$t('tenant')"
                  :no-filter="true"
                  :loading="customerLoading"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details
                  return-object
                  clearable
                  @change="filterUser = null; fetchFilterTenantUsers(); pagination.page=0; fetchLicenses()"
                  @focus="ensureCustomersLoaded()"
                  background-color="white"
                  :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                >
                  <template v-slot:append-item>
                    <div 
                      v-if="customerHasMore && !customerLoading"
                      style="height: 1px;"
                      v-intersect="onCustomerListIntersect"
                    />
                    <div v-else class="px-4 py-2 grey--text text--darken-1 text-caption">
                      {{ customerItems.length ? $t('no_more_results') : $t('no_results') }}
                    </div>
                  </template>
              </v-autocomplete>
            </div>

            <div class="px-4 py-2">
               <v-tooltip bottom :disabled="!!filterTenant">
                  <template v-slot:activator="{ on, attrs }">
                     <div v-bind="attrs" v-on="on">
                        <v-autocomplete
                           v-model="filterUser"
                           :items="filterTenantUsersList"
                           :label="$t('user')"
                           outlined
                           dense
                           hide-details
                           return-object
                           clearable
                           :disabled="!filterTenant"
                           @change="pagination.page=0; fetchLicenses()"
                           background-color="white"
                           :filter="filterTenantUsers"
                           :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                        >
                           <template v-slot:item="{ item }">
                             <div class="d-flex flex-column py-1">
                               <span class="user-name-item">{{ item.name ? item.name : item.personalData.firstname + " " + item.personalData.lastname }}</span>
                               <span class="user-email-item">{{ item.mail }}</span>
                             </div>
                           </template>
                           <template v-slot:selection="{ item }" >
                             {{ item.name ? item.name : item.personalData.firstname + " " + item.personalData.lastname }}
                           </template>
                        </v-autocomplete>
                     </div>
                  </template>
                  <span>{{ $t('please_select_tenant_first') }}</span>
               </v-tooltip>
            </div>
          </v-list>
        </v-menu>

        <v-chip
          v-if="filterType !== null"
          close
          class="mr-2 mt-2"
          @click:close="filterType = null; pagination.page=0; fetchLicenses()"
        >
          {{ filterType ? $t('test_licenses') : $t('normal_licenses') }}
        </v-chip>

        <v-chip
          v-if="filterAssigned !== null"
          close
          class="mt-2"
          @click:close="filterAssigned = null; pagination.page=0; fetchLicenses()"
        >
          {{ filterAssigned ? $t('assigned') : $t('unassigned') }}
        </v-chip>

        <v-chip
          v-if="filterTenant"
          close
          class="mt-2 mr-2"
          @click:close="filterTenant = null; pagination.page=0; fetchLicenses()"
        >
          {{ filterTenant.name }}
        </v-chip>

        <v-chip
          v-if="filterUser"
          close
          class="mt-2"
          @click:close="filterUser = null; pagination.page=0; fetchLicenses()"
        >
          {{ getUserDisplayNameFromData(filterUser) }}
        </v-chip>

      </div>
      <div class="col text-right">
        <!-- Super Admin Creation Controls -->
        <v-btn
          v-if="rightGlobalAccess"
          :ripple="false"
          hide-details="auto"
          @click="isCreatingNormalLicense = false; testLicenseDialog.shown = true,testLicenseDialog.isEdit = false"
          class="rounded-0 mt-2"
          outlined
          color="">
          <v-icon class="mr-1">mdi-plus</v-icon> {{ $t('create_license') }}
        </v-btn>

        <!-- Regular User Button -->
        <v-btn
          v-if="!rightGlobalAccess"
          :ripple="false"
          hide-details="auto"
          @click="purchaseMoreLicenses"
          class="rounded-0 mt-2"
          outlined
          color="">
          <v-icon class="mr-1">mdi-cart-plus</v-icon> {{$t('purchase_more_licenses')}}
        </v-btn>
      </div>

    </div>

    <div class="page-content">
      <v-simple-table class="licenses-table">
        <thead>
          <tr>
            <!-- Column 1: License (Type Filter) -->
            <th class="text-left text-uppercase clickable-header" @click="changeSort('name')" :class="{'active-sort': sort === 'name'}">
              <div class="d-flex align-center">
                {{ $t("license") }}
                <div class="sort-icon-wrapper ml-1">
                  <v-icon x-small v-if="sort === 'name'">{{ order === 'ASC' ? 'mdi-arrow-down':'mdi-arrow-up' }}</v-icon>
                </div>
              </div>
            </th>

            <th v-if="rightGlobalAccess" class="text-left text-uppercase clickable-header" @click="changeSort('customer.name')" :class="{'active-sort': sort === 'customer.name'}">
              <div class="d-flex align-center">
                {{ $t("tenant") }}
                <div class="sort-icon-wrapper ml-1">
                  <v-icon x-small v-if="sort === 'customer.name'">{{ order === 'ASC' ? 'mdi-arrow-down' :'mdi-arrow-up' }}</v-icon>
                </div>
              </div>
            </th>

            <!-- Column 2: Valid From (Sort) -->
            <th class="text-left text-uppercase clickable-header" @click="changeSort('startDate')" :class="{'active-sort': sort === 'startDate'}">
              <div class="d-flex align-center">
                {{ $t("valid_from") }}
                <div class="sort-icon-wrapper ml-1">
                  <v-icon x-small v-if="sort === 'startDate'">{{ order === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                </div>
              </div>
            </th>

            <!-- Column 3: Valid Until (Sort) -->
            <th class="text-left text-uppercase clickable-header" @click="changeSort('endDate')" :class="{'active-sort': sort === 'endDate'}">
              <div class="d-flex align-center">
                {{ $t("valid_until") }}
                <div class="sort-icon-wrapper ml-1">
                  <v-icon x-small v-if="sort === 'endDate'">{{ order === 'ASC' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                </div>
              </div>
            </th>

            <!-- Column 4: Assigned To (Assigned Filter) -->
            <th class="text-left text-uppercase">
              {{ $t("assigned_to") }}
            </th>

            <th class="text-left text-uppercase">{{ $t("actions") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="license in licenses" :key="license.id">
            <td>
              <div class="license-info">
                <div class="license-name">
                  {{ license.name }}
                  <v-chip
                    v-if="license.isTest"
                    x-small
                    outlined
                    color="warning"
                    class="ml-2 test-badge"
                  >
                    <v-icon x-small left>mdi-alert-circle-outline</v-icon>
                    TEST
                  </v-chip>
                  <v-chip
                    v-if="license.cancelled && !checkExpiry(license)"
                    x-small
                    outlined
                    color="error"
                    class="ml-2 cancelled-indicator"
                  >
                    <v-icon x-small left>mdi-cancel</v-icon>
                    {{ $t('cancelled') }}
                  </v-chip>
                  <v-chip
                      v-if="checkExpiry(license)"
                      x-small
                      outlined
                      color="error"
                      class="ml-2 cancelled-indicator"
                  >
                    <v-icon x-small left>mdi-cancel</v-icon>
                    {{ $t('expired') }}
                  </v-chip>
                </div>
                <div class="license-code text-caption text-primary d-flex align-center">
                  {{ license.code }}
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                    </template>
                    <span>Eindeutige Nummer des Lizenzschlüssels</span>
                  </v-tooltip>
                </div>
              </div>
            </td>
            <td v-if="rightGlobalAccess">
              {{ license.customer ? license.customer.name : '-' }}
            </td>
            <td>{{ formatDate(license.startDate) }}</td>
            <td :class="{ 'text-error font-weight-medium': license.cancelledAt }">
              {{ formatDate(license.endDate) }}
            </td>
            <td>
              <template v-if="license.user">
                <div class="assigned-info">
                  <div class="assigned-name">{{ getUserDisplayName(license.user) }}</div>
                  <div class="assigned-code text-caption text-primary d-flex align-center" v-if="license.deviceIdentifier && !license.deviceIdentifier.startsWith('UNASSIGNED')">
                    {{ license.deviceIdentifier }}
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>Geräte-ID der zugewiesenen Lizenz</span>
                    </v-tooltip>
                  </div>
                  <div class="assigned-code text-caption text-primary" v-else>
                    {{ $t("no_device_linked_yet") }}
                  </div>
                </div>
              </template>
              <template v-else>
                <span class="not-assigned">{{ $t("not_assigned") }}</span>
              </template>
            </td>
            <td>
              <div class="actions-container">

                <v-btn
                  v-if="rightGlobalAccess || isTenantAdmin()"
                  icon
                  small
                  outlined
                  class="action-btn edit-btn mr-2"
                  @click="openEditLicense(license)"
                  :title="$t('edit_license')"
                >
                  <v-icon small color="info">mdi-pencil-outline</v-icon>
                </v-btn>

                <v-btn
                  v-if="rightGlobalAccess && !license.stripe"
                  icon
                  small
                  outlined
                  class="action-btn delete-btn mr-2"
                  @click="deleteLicense(license)"
                  :title="$t('delete_license')"
                >
                  <v-icon small color="error">mdi-trash-can-outline</v-icon>
                </v-btn>


                <v-btn
                  v-if="license.assignedTo"
                  icon
                  small
                  outlined
                  class="action-btn revoke-btn mr-2"
                  @click="unassignLicense(license)"
                  :title="$t('unassign_license')"
                >
                  <v-icon small color="error">mdi-close-circle-outline</v-icon>
                </v-btn>

                <v-btn
                  v-else
                  icon
                  small
                  outlined
                  :disabled="checkExpiry(license)"
                  class="action-btn assign-btn mr-2"
                  :class="{ 'disabled-btn': checkExpiry(license) }"
                  @click="openAssignDialog(license)"
                  :title="$t('assign_license')"
                >
                  <v-icon small color="success">mdi-account-plus-outline</v-icon>
                </v-btn>

                <v-btn
                  v-if="!license.cancelledAt && !license.isTest"
                  icon
                  small
                  outlined
                  class="action-btn cancel-btn"
                  @click="openCancelDialog(license)"
                  :title="$t('cancel_subscription')"
                >
                  <v-icon small color="warning">mdi-calendar-remove-outline</v-icon>
                </v-btn>
                <v-tooltip bottom v-else-if="license.cancelledAt && !license.isTest">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      <v-btn
                        icon
                        small
                        outlined
                        class="action-btn renew-btn"
                        :class="{ 'disabled-btn': checkExpiry(license) }"
                        :disabled="checkExpiry(license)"
                        @click="!checkExpiry(license) ? renewSubscription(license) : null"
                      >
                        <v-icon small :color="!checkExpiry(license) ? 'success' : 'grey'">mdi-refresh</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span>{{ !checkExpiry(license) ? $t('renew_subscription') : $t('license_cannot_be_renewed') }}</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Infinite Scroll Trigger -->
      <div v-intersect.quiet="infiniteScrolling" style="height: 1px;"></div>

      <!-- Loading Indicator -->
      <div v-if="pagination.loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <!-- End of List Message -->
      <div v-if="!pagination.hasMore && licenses.length > 0" class="text-center py-4 text--secondary">
        <v-icon small class="mr-2">mdi-check-circle-outline</v-icon>
        {{ $t("all_licenses_loaded") }}
      </div>
    </div>

    <!-- Confirmation Popup -->
    <v-dialog v-model="confirmation.shown" width="500">
      <v-card class="rounded-0">
        <v-card-title class="text-subtitle-1 font-weight-medium py-4">
          {{ $t("security_question") }}
        </v-card-title>

        <v-card-text>
          {{ confirmation.text }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-0" @click="confirmation.shown = false">
            {{ $t("no") }}
          </v-btn>
          <v-btn
            color="primary"
            class="rounded-0"
            @click="
              confirmation.action();
              confirmation.shown = false;
            "
          >
            {{ $t("yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign License Dialog -->
    <v-dialog v-model="assignDialog.shown" width="600">
      <v-card class="rounded-0">
        <v-card-title class="primary white--text py-4">
          <v-icon left color="white">mdi-account-plus-outline</v-icon>
          {{ $t("assign_license") }}
        </v-card-title>

        <v-card-text class="pt-6 pb-4">
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">{{ $t("license") }}:</div>
            <div class="license-info-dialog">
              <div class="license-name">{{ assignDialog.license ? assignDialog.license.name : '' }}</div>
              <div class="license-code text-caption text-primary">{{ assignDialog.license ? assignDialog.license.code : '' }}</div>
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div v-if="assignDialog.loading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <template v-else>
            <div class="text-subtitle-2 mb-2">{{ $t("select_user") }}:</div>
            <v-autocomplete
              v-model="assignDialog.selectedUser"
              :items="assignDialog.users"
              :label="$t('user')"
              :placeholder="$t('search_user_placeholder')"
              outlined
              dense
              item-value="id"
              return-object
              prepend-inner-icon="mdi-magnify"
              class="rounded-0 assign-user-select"
              hide-details="auto"
              :menu-props="{ contentClass: 'assign-user-menu white-dropdown', offsetY: true, maxHeight: 300 }"
              background-color="white"
              :filter="filterUsers"
              clearable
              no-data-text=""
            >
              <template v-slot:item="{ item }">
                <div class="assign-user-item d-flex align-center justify-space-between py-2" style="width: 100%;">
                  <div class="d-flex flex-column">
                    <span class="user-name-item">{{ getUserDisplayNameFromData(item) }}</span>
                    <span class="user-email-item">{{ item.mail }}</span>
                  </div>
                  <v-chip
                    v-if="item.hasLicense"
                    x-small
                    outlined
                    color="warning"
                    class="ml-2 rounded-0"
                  >
                    <v-icon x-small left>mdi-key</v-icon>
                    {{ item.assignedLicenseName || $t('has_license') }}
                  </v-chip>
                </div>
              </template>
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center">
                  <span>{{ getUserDisplayNameFromData(item) }}</span>
                  <v-chip
                    v-if="item.hasLicense"
                    x-small
                    outlined
                    color="warning"
                    class="ml-2 rounded-0"
                  >
                    <v-icon x-small left>mdi-key</v-icon>
                  </v-chip>
                </div>
              </template>
              <template v-slot:no-data>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="text--secondary">
                      {{ $t("no_users_found") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>

            <v-alert
              v-if="assignDialog.selectedUser && assignDialog.selectedUser.hasLicense"
              type="warning"
              dense
              outlined
              class="mt-4 mb-0 rounded-0"
            >
              <div class="text-body-2">
                {{ $t("user_already_has_license_warning") }}
              </div>
            </v-alert>

            <div v-if="assignDialog.users.length === 0" class="text-center py-4 text--secondary">
              {{ $t("no_users_available") }}
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-spacer></v-spacer>
          <v-btn text class="rounded-0" @click="closeAssignDialog">
            <v-icon left small>mdi-close</v-icon>
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            class="rounded-0"
            :disabled="!assignDialog.selectedUser"
            :loading="assignDialog.saving"
            @click="confirmAssignLicense"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ $t("assign") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel Subscription Dialog -->
    <v-dialog v-model="cancelDialog.shown" width="550">
      <v-card class="rounded-0">
        <v-card-title class="warning white--text py-4">
          <v-icon left color="white">mdi-alert-circle-outline</v-icon>
          {{ $t("cancel_subscription") }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="cancelDialog.license" class="cancel-dialog-content">
            <div class="license-info-cancel mb-4">
              <div class="text-subtitle-2 mb-2">{{ $t("license") }}:</div>
              <div class="d-flex align-center">
                <div>
                  <div class="license-name font-weight-medium">{{ cancelDialog.license.name }}</div>
                  <div class="license-code text-caption text-primary">{{ cancelDialog.license.code }}</div>
                </div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="cancellation-details pa-4 mb-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2">{{ $t("subscription_type") }}:</span>
                <v-chip small :color="cancelDialog.license.billingPeriod === 'yearly' ? 'primary' : 'secondary'" outlined class="rounded-0">
                  {{ cancelDialog.license.billingPeriod === 'yearly' ? $t('yearly') : $t('monthly') }}
                </v-chip>
              </div>
              <div class="d-flex justify-space-between align-center">
                <span class="text-body-2">{{ $t("access_until") }}:</span>
                <span class="font-weight-medium">{{ formatDate(cancelDialog.license.endDate) }}</span>
              </div>
            </div>

            <div class="pa-3 mb-3" style="background-color: #FFF3E0; border-left: 4px solid #FF9800;">
              <div style="font-size: 13px; color: #424242;">
                <strong>{{ $t("annual_license_commitment") }}</strong><br>
                <ul style="margin-top: 8px; margin-bottom: 0;">
                  <li>{{ $t("cancel_stops_billing") }}</li>
                  <li>{{ $t("cancel_keeps_access") }}</li>
                  <li v-if="cancelDialog.license && cancelDialog.license.billingPeriod === 'yearly'">
                    <strong>{{ $t("cancel_no_refund") }}</strong>
                  </li>
                  <li v-else>{{ $t("cancel_monthly_stopped") }}</li>
                  <li>{{ $t("cancel_can_reactivate") }}</li>
                </ul>
              </div>
            </div>

            <v-alert
              type="warning"
              dense
              outlined
              class="mb-0 rounded-0"
            >
              <div class="text-body-2">
                {{ $t("cancel_subscription_warning") }}
              </div>
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-btn
            text
            class="rounded-0"
            @click="closeCancelDialog"
          >
            {{ $t("keep_subscription") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="warning"
            class="rounded-0"
            :loading="cancelDialog.loading"
            @click="confirmCancelSubscription"
          >
            <v-icon left small>mdi-calendar-remove</v-icon>
            {{ $t("confirm_cancellation") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Renew Subscription Dialog -->
    <v-dialog v-model="renewDialog.shown" width="550">
      <v-card class="rounded-0">
        <v-card-title class="success white--text py-4">
          <v-icon left color="white">mdi-refresh</v-icon>
          {{ $t("renew_subscription") }}
        </v-card-title>

        <v-card-text class="pt-4">
          <div v-if="renewDialog.license" class="renew-dialog-content">
            <div class="license-info-renew mb-4">
              <div class="text-subtitle-2 mb-2">{{ $t("license") }}:</div>
              <div class="d-flex align-center">
                <div>
                  <div class="license-name font-weight-medium">{{ renewDialog.license.name }}</div>
                  <div class="license-code text-caption text-primary">{{ renewDialog.license.code }}</div>
                </div>
              </div>
            </div>

            <v-divider class="mb-4"></v-divider>

            <div class="renewal-details pa-4 mb-4">
              <div class="d-flex justify-space-between align-center mb-2">
                <span class="text-body-2">{{ $t("subscription_type") }}:</span>
                <v-chip small :color="renewDialog.license.billingPeriod === 'yearly' ? 'primary' : 'secondary'" outlined class="rounded-0">
                  {{ renewDialog.license.billingPeriod === 'yearly' ? $t('yearly') : $t('monthly') }}
                </v-chip>
              </div>
              <div class="d-flex justify-space-between align-center">
                <span class="text-body-2">{{ $t("next_billing_date") }}:</span>
                <span class="font-weight-medium">{{ formatDate(renewDialog.license.endDate) }}</span>
              </div>
            </div>

            <v-alert
              type="info"
              dense
              outlined
              class="mb-0 rounded-0"
            >
              <div class="text-body-2">
                <v-icon small left color="info">mdi-credit-card-outline</v-icon>
                {{ $t("renew_subscription_combined_info") }}
              </div>
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="px-6 py-4">
          <v-btn
            text
            class="rounded-0"
            @click="closeRenewDialog"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            class="rounded-0"
            :loading="renewDialog.loading"
            @click="confirmRenewSubscription"
          >
            <v-icon left small>mdi-refresh</v-icon>
            {{ $t("confirm_renewal") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- Buy Licenses Dialog -->
    <v-dialog v-model="buyLicensesDialog" max-width="700px">
      <v-card class="rounded-0">
        <v-toolbar flat dense color="white">
          <v-spacer></v-spacer>
          <v-btn icon @click="buyLicensesDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-0 pb-6">
          <SubscriptionPlans v-if="buyLicensesDialog" modeProp="add-licenses" :dialogMode="true" @close="buyLicensesDialog = false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- SUPER ADMIN ONLY -->
    <v-dialog v-model="testLicenseDialog.shown" width="600" persistent>
        <v-card class="test-license-dialog rounded-0">
          <v-card-title class="primary white--text py-4">
            <v-icon left color="white">mdi-key-plus</v-icon>
            <!-- Normal license creation disabled - only test licenses allowed -->
            {{!testLicenseDialog.isEdit ? $t('create_test_license'):$t('edit_test_license') }} 
          </v-card-title>

          <v-card-text class="pt-6 pb-2">
            <v-form v-model="testLicenseDialog.isValid" ref="testLicenseForm">
              <div class="form-section mb-4">
                <div class="section-label mb-3">
                  <v-icon small color="primary" class="mr-2">mdi-information-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-medium">{{ $t('license_details') }}</span>
                </div>

                <!-- License type selection disabled - Super Admin should only create test licenses
                <v-select
                  v-model="isCreatingNormalLicense"
                  :items="licenseCreationTypes"
                  :label="$t('license_type')"
                  outlined
                  dense
                  hide-details="auto"
                  class="mb-4 rounded-0"
                  prepend-inner-icon="mdi-card-account-details-outline"
                  background-color="white"
                  :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                ></v-select>
                -->

                <v-text-field
                  v-model="testLicenseDialog.license.name"
                  :label="$t('name')"
                  outlined
                  dense
                  hide-details="auto"
                  class="rounded-0"
                  :class="{'mb-4': rightGlobalAccess}"
                  prepend-inner-icon="mdi-tag-outline"
                  :rules="[v => !!v || $t('name_required')]"
                />
              </div>

              <div class="form-section mb-4" v-if="!testLicenseDialog.isEdit && rightGlobalAccess">
                <div class="section-label mb-3">
                  <v-icon small color="primary" class="mr-2">mdi-account-group-outline</v-icon>
                  <span class="text-subtitle-2 font-weight-medium">{{ $t('assignment') }}</span>
                </div>

                <v-autocomplete
                  v-model="testLicenseDialog.license.customer"
                  :items="customerItems"
                  :search-input.sync="dialogCustomerSearch"
                  :no-filter="true"
                  :loading="customerLoading"
                  :label="$t('customer')"
                  :placeholder="$t('search_placeholder')"
                  outlined
                  hide-details="auto"
                  item-text="name"
                  item-value="id"
                  return-object
                  prepend-inner-icon="mdi-magnify"
                  class="mb-4 rounded-0 custom-input-spacing"
                  @focus="ensureCustomersLoaded()"
                  @change="fetchTenantUsers"
                  :rules="[v => !!v || $t('customer_required')]"
                  :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                  background-color="white">
                  <template v-slot:append-item>
                    <div
                      v-if="customerHasMore && !customerLoading"
                      style="height: 1px;"
                      v-intersect="onCustomerListIntersect"
                    />
                    <div v-else class="px-4 py-2 grey--text text--darken-1 text-caption">
                      {{ customerItems.length ? $t('no_more_results') : $t('no_results') }}
                    </div>
                  </template>
                </v-autocomplete>

                <v-autocomplete
                  v-model="testLicenseDialog.license.user"
                  :items="tenantUsers"
                  :label="$t('user') + ' (' + $t('optional') + ')'"
                  :placeholder="$t('search_user_placeholder')"
                  outlined
                  hide-details="auto"
                  item-value="id"
                  prepend-inner-icon="mdi-magnify"
                  class="rounded-0 custom-input-spacing"
                  :disabled="!testLicenseDialog.license.customer"
                  clearable
                  :menu-props="{ contentClass: 'white-dropdown', offsetY: true }"
                  background-color="white"
                  :filter="filterTenantUsers"
                >
                  <template v-slot:item="{ item }">
                    <div class="d-flex flex-column py-1">
                      <span class="user-name-item">{{ item.name ? item.name : item.personalData.firstname + " " + item.personalData.lastname }}</span>
                      <span class="user-email-item">{{ item.mail }}</span>
                    </div>
                  </template>
                  <template v-slot:selection="{ item }" >
                    {{ item.name ? item.name : item.personalData.firstname + " " + item.personalData.lastname }}
                  </template>
                </v-autocomplete>
              </div>

              <div class="form-section" v-if="rightGlobalAccess">
                <div class="section-label mb-3">
                  <v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
                  <span class="text-subtitle-2 font-weight-medium">{{ $t('validity_period') }}</span>
                </div>

                <v-row>
                  <v-col cols="12" sm="6" class="py-0">
                    <v-menu
                      ref="startDateMenu"
                      v-model="testLicenseDialog.startDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDateForDisplay(testLicenseDialog.license.startDate)"
                          :label="$t('start_date')"
                          prepend-inner-icon="mdi-calendar-start"
                          readonly
                          outlined
                          dense
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-4 rounded-0"
                          :rules="[v => !!v || $t('start_date_required')]"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="testLicenseDialog.license.startDate"
                        @input="onStartDateChange"
                        :min="new Date().toISOString().substr(0, 10)"
                        color="primary"
                        locale="de-DE"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="6" class="py-0">
                    <v-menu
                      ref="endDateMenu"
                      v-model="testLicenseDialog.endDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDateForDisplay(testLicenseDialog.license.endDate)"
                          :label="$t('end_date')"
                          prepend-inner-icon="mdi-calendar-end"
                          readonly
                          outlined
                          dense
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                          class="mb-4 rounded-0"
                          :rules="[v => !!v || $t('end_date_required')]"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="testLicenseDialog.license.endDate"
                        @input="onEndDateChange"
                        :min="testLicenseDialog.license.startDate"
                        color="primary"
                        locale="de-DE"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="testLicenseDialog.license.startDate && testLicenseDialog.license.endDate"
                  type="info"
                  dense
                  outlined
                  class="mt-2 mb-0 rounded-0"
                >
                  <div class="text-caption">
                    {{ $t('license_duration') }}: {{ calculateDuration() }} {{ $t('days') }}
                  </div>
                </v-alert>
              </div>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="px-6 py-4">
            <v-spacer></v-spacer>
            <v-btn
              text
              class="rounded-0"
              @click="testLicenseDialog.shown = false; testLicenseDialog.license = resetLicense();testLicenseDialog.isEdit = false"
            >
              <v-icon left small>mdi-close</v-icon>
              {{ $t("cancel") }}
            </v-btn>

            <v-btn
              v-if="testLicenseDialog.isEdit"
              color="primary"
              :disabled="!testLicenseDialog.isValid"
              @click="rightGlobalAccess ? editLicense() : updateName()"
              class="px-6 rounded-0"
            >
              <v-icon left small>mdi-check</v-icon>
              {{ $t("save") }}
            </v-btn>
            
            <v-btn
              v-else
              color="primary"
              :disabled="!testLicenseDialog.isValid"
              @click="createLicense()"
              class="px-6 rounded-0"
            >
              <v-icon left small>mdi-check</v-icon>
              {{ $t("create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </div>
</template>

<script>
import SubscriptionPlans from './SubscriptionPlans.vue';

export default {
  name: "Licenses",

  components: {
    SubscriptionPlans
  },

  data() {
    return {
      user: null,
      licenses: [],
      pagination: {
        page: 0,
        size: 200,
        totalElements: 0,
        totalPages: 0,
        hasMore: true,
        loading: false
      },
      confirmation: {
        text: "",
        action: null,
        shown: false,
      },
      assignDialog: {
        shown: false,
        license: null,
        selectedUser: null,
        users: [],
        loading: false,
        saving: false,
      },
      cancelDialog: {
        shown: false,
        license: null,
        loading: false,
      },
      isCreatingNormalLicense: false,
      renewDialog: {
        shown: false,
        license: null,
        loading: false,
      },
      buyLicensesDialog: false,
      testLicenseDialog:{
        isEdit:false,
        shown: false,
        isValid: false,
        startDateMenu: false,
        endDateMenu: false,
        license: {
          licenseKey: "",
          name: "",
          user: null,
          customer: null,
          startDate: new Date().toISOString().substr(0, 10),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
          testLicense: true,
          canceled: false
        }
      },
      tenantUsers:[],
      customers:[],
      rightGlobalAccess: false,
      sort: 'customer.name',
      order: 'ASC',
      filterType: null, // null = all, true = test, false = normal
      filterExpired: null,
      filterAssigned: null, // null = all, true = assigned, false = unassigned
      filterTenant: null,
      filterUser: null,
      filterTenantUsersList: [],
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) =>
          /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) ||
          "E-mail must be valid",
      ],

      customerItems: [],
      filterCustomerSearch: "",
      dialogCustomerSearch: "",
      customerLoading: false,
      customerPage: 0,
      customerSize: 7,
      customerTotalPages: 1,
      customerHasMore: true,
      customerDebounceTimer: null,
      lastCustomerQueryKey: null,
      customerSearch:"",
    };
  },

  computed: {
    licenseCreationTypes() {
      return [
        { text: this.$t('test_licenses'), value: false },
        // Normal license creation disabled - Super Admin should only create test licenses
        // { text: this.$t('normal_licenses'), value: true }
      ];
    },
    testLicensesCount() {
      return this.licenses.filter(l => l.isTest).length;
    }
  },

  watch: {
    filterCustomerSearch() {
      this.queueCustomerFetch("filter");
    },
    dialogCustomerSearch() {
      this.queueCustomerFetch("dialog");
    }
  },

  mounted() {

    this.fetchLicenses();
    this.fetchMe();

    
    // Check for Stripe checkout session
    if (this.$route.query.session_id) {
      this.buyLicensesDialog = true;
    }
  },

  methods: {

    gotCurrentUser(response) {
      this.user = response.data;
    },
    async fetchMe() {
      var _this = this;
      await this.$axios
        .get("api/me")
        .then(function (response) {
          _this.me = response.data;
          _this.user = response.data;
          _this.rightGlobalAccess = _this.hasRight("GlobalAccess");
          if(_this.rightGlobalAccess) {
            _this.fetchCustomers();
          } else {
            // If Tenant Admin, set filterTenant to own tenant and fetch users
            if (_this.me.customer) {
              _this.filterTenant = _this.me.customer;
              _this.fetchFilterTenantUsers();
            }
          }
        })
        .catch(this.onError);

    },
    hasRight(right) {
      if (this.me.rights.includes(right)) return true;
      if (this.me.role != null) {
        return this.me.role.rights.includes(right);
      }
      return false;
    },
    resetLicense(){
      return {
          licenseKey: "",
          name: "",
          user: null,
          customer: null,
          startDate: new Date().toISOString().substr(0, 10),
          endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10),
          testLicense: true,
          canceled: false
        }
    },
    formatDateForDisplay(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    onStartDateChange(date) {
      this.testLicenseDialog.startDateMenu = false;
      this.testLicenseDialog.license.startDate = date;
    },
    onEndDateChange(date) {
      this.testLicenseDialog.endDateMenu = false;
      this.testLicenseDialog.license.endDate = date;
    },
    openEditLicense(license) {
      this.testLicenseDialog.shown = true;
      this.testLicenseDialog.isEdit = true;

      const copy = { ...license };
      copy.startDate = (copy.startDate || "").substring(0, 10);
      copy.endDate = (copy.endDate || "").substring(0, 10);    

      this.testLicenseDialog.license = copy;
    },
    calculateDuration() {
      if (!this.testLicenseDialog.license.startDate || !this.testLicenseDialog.license.endDate) {
        return 0;
      }
      const start = new Date(this.testLicenseDialog.license.startDate);
      const end = new Date(this.testLicenseDialog.license.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    fetchLicenses(append = false) {
      if (this.pagination.loading) return;

      var _this = this;
      this.pagination.loading = true;

      this.$axios
        .get("api/licenses", {
          params: {
            page: this.pagination.page,
            size: this.pagination.size,
            sort: this.sort,
            order: this.order,
            testLicense: this.filterType,
            assigned: this.filterAssigned,
            tenantId: this.filterTenant ? this.filterTenant.id : null,
            userId: this.filterUser ? this.filterUser.id : null,
            expired: this.filterExpired
          }
        })
        .then(function (response) {
          // Update pagination info
          _this.pagination.totalElements = response.data.totalElements;
          _this.pagination.totalPages = response.data.totalPages;
          _this.pagination.hasMore = (_this.pagination.page + 1) < response.data.totalPages;

          // Map backend field names to frontend expectations
          const mappedLicenses = response.data.content
            .filter(license => {
              // Filter out superadmin licenses (hide from everyone, including superadmins)
              // A superadmin license has:
              // 1. Name "Superadmin License" OR
              // 2. testLicense = false AND name contains "Superadmin" OR
              // 3. User has GlobalAccess right (check if user object exists and has rights)
              const isSuperadminLicense = 
                license.name === 'Superadmin License' ||
                (!license.testLicense && license.name && license.name.toLowerCase().includes('superadmin'));
              
              // Check if assigned user has GlobalAccess right
              const userHasGlobalAccess = license.user && 
                license.user.rights && 
                license.user.rights.includes('GlobalAccess');
              
              // Hide if it's a superadmin license OR if assigned to a user with GlobalAccess
              return !isSuperadminLicense && !userHasGlobalAccess;
            })
            .map(license => {
              // Prefer current subscription billing; fall back to license data for legacy records.
              let billingPeriod = license.subscriptionBillingInterval || license.billingInterval || 'monthly';
              if (license.startDate && license.endDate) {
                const start = new Date(license.startDate);
                const end = new Date(license.endDate);
                const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
                // If duration is more than 60 days, it's likely a yearly subscription
                billingPeriod = license.subscriptionBillingInterval || license.billingInterval || (diffDays > 60 ? 'yearly' : 'monthly');
              }
              return {
                ...license,
                isTest: license.testLicense,
                cancelledAt: license.cancelled ? new Date() : null,
                code: license.licenseKey,
                assignedTo: license.user,
                billingPeriod: billingPeriod
              };
            });

          // Append or replace licenses
          if (append) {
            _this.licenses = [..._this.licenses, ...mappedLicenses];
          } else {
            _this.licenses = mappedLicenses;
          }
        })
        .catch(this.onError)
        .finally(function() {
          _this.pagination.loading = false;
          showLoadingCircle(false);
        });
    },
    infiniteScrolling(entries, observer, isIntersecting) {
      if (isIntersecting && this.pagination.hasMore && !this.pagination.loading) {
        this.pagination.page++;
        this.fetchLicenses(true);
      }
    },
    getUserDisplayName(user) {
      if (!user) return '';
      // Check if name exists and is not empty/whitespace
      if (user.name && user.name.trim() !== '') {
        return user.name;
      }
      // Fall back to personalData firstname + lastname
      if (user.personalData) {
        return user.personalData.firstname + " " + user.personalData.lastname;
      }
      return '';
    },
    getUserDisplayNameFromData(userData) {
      if (!userData) return '';
      // Check if name exists and is not empty/whitespace
      if (userData.name && userData.name.trim() !== '') {
        return userData.name;
      }
      // Fall back to personalData firstname + lastname
      if (userData.personalData) {
        return userData.personalData.firstname + " " + userData.personalData.lastname;
      }
      return userData.mail || '';
    },
    filterUsers(item, queryText) {
      if (!queryText) return true;
      const search = queryText.toLowerCase();
      const name = this.getUserDisplayNameFromData(item).toLowerCase();
      const email = (item.mail || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    },
    filterTenantUsers(item, queryText) {
      if (!queryText) return true;
      const search = queryText.toLowerCase();
      let name = '';
      if (item.name) {
        name = item.name.toLowerCase();
      } else if (item.personalData) {
        name = (item.personalData.firstname + ' ' + item.personalData.lastname).toLowerCase();
      }
      const email = (item.mail || '').toLowerCase();
      return name.includes(search) || email.includes(search);
    },
    fetchCustomers() {
      if(!this.rightGlobalAccess) return;
      var _this = this;
      this.$axios
        .get("api/customers")
        .then(function (response) {
          _this.customers = response.data.content;
        })
        .catch(this.onError)
        .finally();
    },
    resetCustomerPagination() {
      const selectedA = this.filterTenant;
      const selectedB = this.testLicenseDialog?.license?.customer;

      this.customerItems = [];
      this.customerPage = 0;
      this.customerTotalPages = 1;
      this.customerHasMore = true;

      const toInject = [selectedA, selectedB].filter(x => x && x.id);
      for (const c of toInject) {
        if (!this.customerItems.some(i => i.id === c.id)) this.customerItems.unshift(c);
      }
    },

    ensureCustomersLoaded() {
      if (!this.rightGlobalAccess) return;
      if (!this.customerItems.length && !this.customerLoading) {
        this.resetCustomerPagination();
        this.fetchCustomersPage();
      }
    },
    queueCustomerFetch(source) {
      clearTimeout(this.customerDebounceTimer);
      this.customerDebounceTimer = setTimeout(() => {
        const search = source === "filter"
          ? (this.filterCustomerSearch ?? "")
          : (this.dialogCustomerSearch ?? "");

        this.customerSearch = search.trim();
        this.resetCustomerPagination();
        this.fetchCustomersPage();
      }, 300);
    },
    async fetchCustomersPage() {
      if (!this.rightGlobalAccess) return;
      if (this.customerLoading) return;
      if (!this.customerHasMore) return;

      const search = (this.customerSearch ?? "").toString().trim();
      const queryKey = `${search}::${this.customerPage}`;
      this.lastCustomerQueryKey = queryKey;

      this.customerLoading = true;
      try {
        const params = { page: this.customerPage, size: this.customerSize };
        const url = search
          ? `api/customers/search/${encodeURIComponent(search)}`
          : `api/customers`;

        const res = await this.$axios.get(url, { params });

        if (this.lastCustomerQueryKey !== queryKey) return;

        const content = res.data?.content || [];
        const totalPages = res.data?.totalPages ?? 1;

        const existing = new Set(this.customerItems.map(c => c.id));
        for (const c of content) {
          if (!existing.has(c.id)) this.customerItems.push(c);
        }

        this.customerTotalPages = totalPages;
        this.customerPage += 1;
        this.customerHasMore = this.customerPage < this.customerTotalPages;
      } catch (e) {
        this.onError(e);
      } finally {
        this.customerLoading = false;
      }
    },

    onCustomerListIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) this.fetchCustomersPage();
    },
    fetchFilterTenantUsers() {
      if (!this.filterTenant) {
        this.filterTenantUsersList = [];
        return;
      }
      const customerId = this.filterTenant?.id;
      if (!customerId) {
        this.filterTenantUsersList = [];
        return;
      }
      var _this = this;
      this.$axios
        .get(`api/users/customer/${customerId}`)
        .then(function (response) {
          _this.filterTenantUsersList = response.data;
        })
        .catch(this.onError)
        .finally();
    },
    fetchTenantUsers(){
      // if(!this.rightGlobalAccess) return;
      // if(this.testLicenseDialog.license.customer) return
      const selectedCustomer = this.testLicenseDialog.license.customer;
      const customerId = selectedCustomer?.id;
      if (!customerId) {
        this.tenantUsers = [];
        return;
      }
      var _this = this;
      this.$axios
        .get(`api/users/customer/${customerId}`)
        .then(function (response) {
          _this.tenantUsers = response.data;
        })
        .catch(this.onError)
        .finally();
    },
    createLicense(){
      var license = this.testLicenseDialog.license;

      // Convert dates to ISO 8601 format for backend (ZonedDateTime)
      const startDate = new Date(license.startDate);
      const endDate = new Date(license.endDate);

      const data = {
        name: license.name,
        user: license.user,
        customer: typeof license.customer === "number" ? license.customer : license.customer?.id,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        testLicense: !this.isCreatingNormalLicense,
        cancelled: false
      }

      var _this = this;
      this.$axios
        .post("api/licenses", data)
        .then(function(response) {
          _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("license_created"));
          _this.testLicenseDialog.shown = false;
          _this.testLicenseDialog.isEdit = false;
          _this.testLicenseDialog.license = _this.resetLicense();
          // Reset pagination and reload from start
          _this.pagination.page = 0;
          _this.pagination.hasMore = true;
          _this.fetchLicenses();
        })
        .catch(this.onError);
    },
    toIsoZ(dateOnly) {
      return `${dateOnly}T00:00:00Z`;
    },
    editLicense(){
      const l = this.testLicenseDialog.license;

      const data = {
        name: l.name,
        startDate: this.toIsoZ(l.startDate),
        endDate: this.toIsoZ(l.endDate),
      };

      var _this = this;
      this.$axios
        .put("api/licenses/"+ l.id, data)
        .then(function(response) {
          _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("license_updated"));
          _this.testLicenseDialog.shown = false;
          _this.testLicenseDialog.isEdit = false;
          _this.testLicenseDialog.license = _this.resetLicense();
          // Reset pagination and reload from start
          _this.pagination.page = 0;
          _this.pagination.hasMore = true;
          _this.fetchLicenses();
        })
        .catch(this.onError);
    },
    updateName(){
      const l = this.testLicenseDialog.license;

      var _this = this;

      this.$axios.put(`api/licenses/${l.id}/name`, { name: l.name })
        .then(function(response) {
            _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("license_updated"));
            _this.testLicenseDialog.shown = false;
            _this.testLicenseDialog.isEdit = false;
            _this.testLicenseDialog.license = _this.resetLicense();
            // Reset pagination and reload from start
            _this.pagination.page = 0;
            _this.pagination.hasMore = true;
            _this.fetchLicenses();
          })
          .catch(this.onError);
    },
    licenseCreated(){
          _this.$noty.success('<i class="fas fa-check-circle mr-3"></i>' + _this.$t("customer_created"));
          // _this.resetEditedCustomer();
          // _this.fetchCustomers();
    },
    changeSort(column) {
      if (this.sort === column) {
        this.order = this.order === 'ASC' ? 'DESC' : 'ASC';
      } else {
        this.sort = column;
        this.order = 'ASC';
      }
      this.pagination.page = 0;
      this.fetchLicenses();
    },
    getDummyLicenses() {
      return [
        {
          id: 1,
          name: "MUNIMAN Pro",
          code: "LIC-2024-001",
          validFrom: "2024-01-15",
          validUntil: "2025-01-14",
          isTest: false,
          billingPeriod: "yearly",
          cancelledAt: null,
          assignedTo: {
            name: "Max Müller",
            deviceId: "DEV-A1B2C3"
          }
        },
        {
          id: 2,
          name: "MUNIMAN Enterprise",
          code: "LIC-2024-002",
          validFrom: "2024-11-01",
          validUntil: "2024-12-31",
          isTest: true,
          billingPeriod: "monthly",
          cancelledAt: null,
          assignedTo: {
            name: "Anna Schmidt",
            deviceId: "DEV-D4E5F6"
          }
        },
        {
          id: 3,
          name: "MUNIMAN Basic",
          code: "LIC-2024-003",
          validFrom: "2024-03-10",
          validUntil: "2025-03-09",
          isTest: false,
          billingPeriod: "yearly",
          cancelledAt: "2024-12-01",
          assignedTo: null
        },
        {
          id: 4,
          name: "MUNIMAN Pro",
          code: "LIC-2024-004",
          validFrom: "2024-06-20",
          validUntil: "2025-06-19",
          isTest: false,
          billingPeriod: "monthly",
          cancelledAt: null,
          assignedTo: {
            name: "Thomas Weber",
            deviceId: "DEV-G7H8I9"
          }
        },
        {
          id: 5,
          name: "MUNIMAN Enterprise",
          code: "LIC-2024-005",
          validFrom: "2024-12-01",
          validUntil: "2025-01-31",
          isTest: true,
          billingPeriod: "yearly",
          cancelledAt: null,
          assignedTo: null
        }
      ];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },

    deleteLicense(license) {

      const _this = this;
      this.showConfirmation(
        this.$t("confirm_delete_license"),
        function() {
          _this.doDeleteLicense(license);
        }
      );
    },

    doDeleteLicense(license) {
      const _this = this;
      this.$axios.delete(`api/licenses/${license.id}`)
        .then(function(response) {
          _this.$noty.success('<i class="fas fa-check mr-3"></i>' + _this.$t("license_deleted"));
          // Reset pagination and reload from start
          _this.pagination.page = 0;
          _this.pagination.hasMore = true;
          _this.fetchLicenses();
        })
        .catch(function(error) {
          _this.onError(error);
        });
    },

    unassignLicense(license) {
      const _this = this;
      this.showConfirmation(
        this.$t("confirm_unassign_license"),
        function() {
          _this.doUnassignLicense(license);
        }
      );
    },

    doUnassignLicense(license) {
      const _this = this;
      this.$axios.put(`api/licenses/${license.id}/unassign`)
        .then(function(response) {
          _this.$noty.success('<i class="fas fa-check mr-3"></i>' + _this.$t("license_unassigned"));
          // Reset pagination and reload from start
          _this.pagination.page = 0;
          _this.pagination.hasMore = true;
          _this.fetchLicenses();
        })
        .catch(function(error) {
          _this.onError(error);
        });
    },

    openAssignDialog(license) {
      this.assignDialog.license = license;
      this.assignDialog.selectedUser = null;
      this.assignDialog.users = [];
      this.assignDialog.loading = true;
      this.assignDialog.shown = true;

      // Fetch available users for this license
      this.fetchAvailableUsersForLicense(license.id);
    },

    closeAssignDialog() {
      this.assignDialog.shown = false;
      this.assignDialog.license = null;
      this.assignDialog.selectedUser = null;
      this.assignDialog.users = [];
      this.assignDialog.loading = false;
      this.assignDialog.saving = false;
    },

    fetchAvailableUsersForLicense(licenseId) {
      const _this = this;
      this.$axios.get(`api/licenses/${licenseId}/available-users`)
        .then(function(response) {
          _this.assignDialog.users = response.data;
          _this.assignDialog.loading = false;
        })
        .catch(function(error) {
          _this.onError(error);
          _this.assignDialog.loading = false;
        });
    },

    confirmAssignLicense() {
      if (!this.assignDialog.selectedUser || !this.assignDialog.license) return;

      this.assignDialog.saving = true;
      const _this = this;
      const license = this.assignDialog.license;
      const user = this.assignDialog.selectedUser;

      this.$axios.put(`api/licenses/${license.id}/assign`, {
        userId: user.id
      })
        .then(function(response) {
          _this.$noty.success('<i class="fas fa-check mr-3"></i>' + _this.$t("license_assigned"));
          _this.closeAssignDialog();
          // Reset pagination and reload from start
          _this.pagination.page = 0;
          _this.pagination.hasMore = true;
          _this.fetchLicenses();
        })
        .catch(function(error) {
          _this.onError(error);
          _this.assignDialog.saving = false;
        });
    },

    showConfirmation(text, action) {
      this.confirmation.text = text;
      this.confirmation.action = action;
      this.confirmation.shown = true;
    },

    onError(error) {
      if (error.response && error.response.data) {
        this.$noty.error('<i class="fas fa-minus-circle mr-3"></i>' + this.$t(error.response.data.message || error.response.data));
      } else {
        console.error(error);
      }
    },

    purchaseMoreLicenses() {
      this.buyLicensesDialog = true;
    },

    openCancelDialog(license) {
      this.cancelDialog.license = license;
      this.cancelDialog.shown = true;
      this.cancelDialog.loading = false;
    },

    closeCancelDialog() {
      this.cancelDialog.shown = false;
      this.cancelDialog.license = null;
      this.cancelDialog.loading = false;
    },

    confirmCancelSubscription() {
      if (!this.cancelDialog.license) return;

      this.cancelDialog.loading = true;

      // Use the existing PaymentController cancel endpoint
      // Note: Frontend maps licenseKey to 'code', but also keeps it as 'licenseKey' usually.
      // We'll use the licenseKey property.
      const key = this.cancelDialog.license.licenseKey || this.cancelDialog.license.code;

      this.$axios.post(`api/payment/license/${key}/cancel`)
        .then(response => {
          this.$noty.success('<i class="fas fa-check mr-3"></i>' + this.$t("subscription_cancelled_success"));
          this.closeCancelDialog();
          this.fetchLicenses();
        })
        .catch(this.onError)
        .finally(() => {
          this.cancelDialog.loading = false;
        });
    },

    renewSubscription(license) {
      this.renewDialog.license = license;
      this.renewDialog.shown = true;
      this.renewDialog.loading = false;
    },

    isTenantAdmin() {
      // A Tenant Admin has ManageTenantUsers or ManageTenant rights but NOT GlobalAccess
      return (this.hasRight("ManageTenantUsers") || this.hasRight("ManageTenant")) &&
             !this.hasRight("GlobalAccess");
    },
    closeRenewDialog() {
      this.renewDialog.shown = false;
      this.renewDialog.license = null;
      this.renewDialog.loading = false;
    },

    confirmRenewSubscription() {
      if (!this.renewDialog.license) return;

      this.renewDialog.loading = true;

      // Use license key for payment controller endpoint
      const key = this.renewDialog.license.licenseKey || this.renewDialog.license.code;

      this.$axios.post(`api/payment/license/${key}/reactivate`)
       .then(response => {
         this.$noty.success('<i class="fas fa-check mr-3"></i>' + this.$t("subscription_renewed_success"));
         this.closeRenewDialog();
         this.fetchLicenses();
       })
       .catch(this.onError)
       .finally(() => {
         this.renewDialog.loading = false;
       });
    },
    checkExpiry(license){
      const now = new Date();
      const endDate = new Date(license.endDate);
      if (license.cancelled && endDate < now) {
        return true;
      }
      return false;

    },
  },
};
</script>

<style scoped>
.page-content {
  margin-top: 20px;
}

.licenses-table {
  background-color: transparent;
}

.licenses-table thead th {
  font-weight: 600;
  color: #666;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 16px;
}

.license-count {
  margin-left: 8px;
  font-weight: 400;
  color: #999;
  cursor: help;
}

.license-count .test-count {
  color: #ff9800;
  font-weight: 500;
}

.licenses-table tbody tr {
  border-bottom: 1px solid #e0e0e0;
}

.licenses-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.licenses-table tbody td {
  padding: 16px;
  vertical-align: middle;
}

.license-info {
  display: flex;
  flex-direction: column;
}

.license-name {
  font-weight: 500;
  display: flex;
  align-items: center;
}

.license-code {
  color: #0069AE;
  font-size: 0.8rem;
}

.test-badge {
  font-size: 0.65rem;
  height: 20px;
}

.cancelled-indicator {
  font-size: 0.65rem;
  height: 20px;
}

.expires-info {
  margin-top: 2px;
  font-size: 0.75rem;
}

.text-error {
  color: #f44336 !important;
}

.assigned-info {
  display: flex;
  flex-direction: column;
}

.assigned-name {
  font-weight: 500;
}

.assigned-code {
  color: #0069AE;
  font-size: 0.8rem;
}

.not-assigned {
  color: #999;
  font-style: italic;
}

.actions-container {
  display: flex;
  align-items: center;
}

.action-btn {
  border-color: #e0e0e0;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.revoke-btn {
  border-color: #f44336;
}

.revoke-btn:hover {
  background-color: rgba(244, 67, 54, 0.08);
}

.assign-btn {
  border-color: #4caf50;
}

.assign-btn:hover {
  background-color: rgba(76, 175, 80, 0.08);
}
.edit-btn {
  border-color: #2196EA;
}

.edit-btn:hover {
  background-color: rgba(33, 150, 234, 0.08);
}

.cancel-btn {
  border-color: #ff9800;
}

.cancel-btn:hover {
  background-color: rgba(255, 152, 0, 0.08);
}

.disabled-btn {
  border-color: #e0e0e0 !important;
  opacity: 0.5;
  cursor: not-allowed;
}

.disabled-btn:hover {
  background-color: transparent !important;
}

.renew-btn {
  border-color: #4caf50;
}

.renew-btn:hover {
  background-color: rgba(76, 175, 80, 0.08);
}

.licenses-summary {
  color: #0069AE;
  font-size: 0.9rem;
}

.text-primary {
  color: #0069AE !important;
}

/* Assign Dialog Styles */
.license-info-dialog .license-name {
  font-weight: 500;
  font-size: 1rem;
}

.license-info-dialog .license-code {
  color: #0069AE;
  font-size: 0.85rem;
}

.users-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
}

.user-list-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.user-list-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Cancel/Renew Dialog Styles */
.cancellation-details,
.renewal-details {
  background-color: #fafafa;
  border: 1px solid #e0e0e0;
}

/* Dialog consistent styling */
::v-deep .v-dialog .v-card {
  border-radius: 0 !important;
}

::v-deep .v-alert {
  border-radius: 0 !important;
}

::v-deep .v-chip {
  border-radius: 0 !important;
}

::v-deep .v-text-field--outlined fieldset {
  border-radius: 0 !important;
}

.text-success {
  color: #4caf50 !important;
}

/* Test License Dialog Styles */
.test-license-dialog .v-card__title {
  font-size: 1.25rem;
  font-weight: 500;
}

.test-license-dialog .form-section {
  background-color: #fafafa;
  border-radius: 0;
  padding: 16px;
}

.test-license-dialog .section-label {
  display: flex;
  align-items: center;
  color: #424242;
}

.test-license-dialog .v-text-field--outlined .v-input__slot,
.test-license-dialog .v-select--outlined .v-input__slot {
  background-color: white;
}

.test-license-dialog .v-input--is-disabled {
  opacity: 0.6;
}

.test-license-dialog .v-text-field.v-text-field--outlined fieldset,
.test-license-dialog .v-select.v-select--outlined fieldset {
  border-radius: 0;
}

.test-license-dialog .v-text-field.v-text-field--outlined .v-input__control,
.test-license-dialog .v-select.v-select--outlined .v-input__control {
  border-radius: 0;
}

.custom-input-spacing .v-input__slot {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

/* Assign License Dialog Styles */
.assign-user-select {
  background-color: white !important;
}

.assign-user-item {
  width: 100%;
}

.user-name-item {
  font-weight: 500;
  color: #212121 !important;
  font-size: 0.875rem;
}

.user-email-item {
  font-size: 0.75rem;
  color: #757575 !important;
}

.clickable-header {
  cursor: pointer;
  user-select: none;
}

.clickable-header:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.active-sort {
  font-weight: bold;
  color: #0069AE !important; /* var(--main-color) */
}

.sort-icon-wrapper {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

</style>

<style>
/* Global styles for assign user menu (not scoped) */
.assign-user-menu {
  background-color: white !important;
}

.assign-user-menu .v-list {
  background-color: white !important;
  background: white !important;
  padding: 0 !important;
}

.assign-user-menu .v-list-item {
  background-color: white !important;
  border-bottom: 1px solid #f0f0f0;
  min-height: 56px;
}

.assign-user-menu .v-list-item:last-child {
  border-bottom: none;
}

.assign-user-menu .v-list-item:hover {
  background-color: #f5f5f5 !important;
}

.assign-user-menu .v-list-item--active {
  background-color: #e3f2fd !important;
}

.assign-user-menu .v-list-item__content {
  color: #212121 !important;
}
</style>
